import type { Post } from '../contexts/PostsContext';

export interface User {
  id: string;
  username: string;
  profileImage?: string;
}

export interface Tag {
  id: string;
  name: string;
  description?: string;
  category?: string;
  severity?: string;
  icon?: string;
  color?: string;
}

export interface Comment {
  id: string;
  content: string;
  author: User;
  createdAt: string;
  postId: string;
  parentId?: string;
}

/**
 * Normalized entity store for efficient data access
 * Prevents duplicate fetches by storing entities by ID
 */
class EntityStore {
  private posts = new Map<string, Post>();
  private users = new Map<string, User>();
  private tags = new Map<string, Tag>();
  private comments = new Map<string, Comment>();

  /**
   * Get post by ID (from cache, no network call)
   */
  getPost(postId: string): Post | undefined {
    return this.posts.get(postId);
  }

  /**
   * Get user by ID (from cache)
   */
  getUser(userId: string): User | undefined {
    return this.users.get(userId);
  }

  /**
   * Get tag by ID (from cache)
   */
  getTag(tagId: string): Tag | undefined {
    return this.tags.get(tagId);
  }

  /**
   * Get comment by ID (from cache)
   */
  getComment(commentId: string): Comment | undefined {
    return this.comments.get(commentId);
  }

  /**
   * Update store with normalized data
   */
  updateEntities(data: {
    posts?: Post[];
    users?: Record<string, User>;
    tags?: Record<string, Tag>;
    comments?: Comment[];
  }) {
    // Update posts
    if (data.posts) {
      data.posts.forEach(post => {
        this.posts.set(post.id, post);
        // Also store author if included
        if (post.author) {
          const user: User = {
            id: post.author.id,
            username: post.author.username,
          };
          // Convert null to undefined for optional property
          if (
            post.author.profileImage !== undefined &&
            post.author.profileImage !== null
          ) {
            user.profileImage = post.author.profileImage;
          }
          this.users.set(post.author.id, user);
        }
      });
    }

    // Update users map
    if (data.users) {
      Object.values(data.users).forEach(user => {
        this.users.set(user.id, user);
      });
    }

    // Update tags map
    if (data.tags) {
      Object.values(data.tags).forEach(tag => {
        this.tags.set(tag.id, tag);
      });
    }

    // Update comments
    if (data.comments) {
      data.comments.forEach(comment => {
        this.comments.set(comment.id, comment);
        // Store comment author
        if (comment.author) {
          this.users.set(comment.author.id, comment.author);
        }
      });
    }
  }

  /**
   * Get multiple posts by IDs (all from cache)
   */
  getPosts(postIds: string[]): Post[] {
    return postIds
      .map(id => this.posts.get(id))
      .filter((post): post is Post => post !== undefined);
  }

  /**
   * Get multiple users by IDs (all from cache)
   */
  getUsers(userIds: string[]): User[] {
    return userIds
      .map(id => this.users.get(id))
      .filter((user): user is User => user !== undefined);
  }

  /**
   * Update a single post
   */
  updatePost(postId: string, updates: Partial<Post>) {
    const existing = this.posts.get(postId);
    if (existing) {
      this.posts.set(postId, { ...existing, ...updates });
    }
  }

  /**
   * Remove a post
   */
  removePost(postId: string) {
    this.posts.delete(postId);
  }

  /**
   * Clear all entities
   */
  clear() {
    this.posts.clear();
    this.users.clear();
    this.tags.clear();
    this.comments.clear();
  }

  /**
   * Clear posts only (keep users, tags, comments)
   */
  clearPosts() {
    this.posts.clear();
  }

  /**
   * Get all posts (for debugging)
   */
  getAllPosts(): Post[] {
    return Array.from(this.posts.values());
  }

  /**
   * Get store stats (for debugging)
   */
  getStats() {
    return {
      posts: this.posts.size,
      users: this.users.size,
      tags: this.tags.size,
      comments: this.comments.size,
    };
  }
}

export const entityStore = new EntityStore();
